import { query, where } from 'firebase/firestore'
import type { MembershipRequest, MembershipRequestId } from '#shared/types/MembershipRequest'
import type { SkillId } from '#shared/types/Skill'
import type { UserId } from '#shared/types/ids'
import { orderByDate } from '#shared/utils'
import { useFirebase } from '@/composables/firebase/useFirebase'
import { useApi } from '@/composables/api/useApi'

/**
 * Composable for membership requests functionality
 */
export function useMembershipRequests() {
    const { $firebase, collections } = useFirebase()
    const { api } = useApi()

    /**
     * List membership requests for a skill
     */
    const listMembershipRequests = async (skillId: SkillId): Promise<MembershipRequest[]> => {
        const membershipRequests = await $firebase.listDocuments<MembershipRequest>(
            query(collections.membershipRequests, where('skillId', '==', skillId))
        )

        return orderByDate(membershipRequests)
    }

    /**
     * Load a membership request by ID
     */
    const loadMembershipRequest = async (id: MembershipRequestId): Promise<MembershipRequest> => {
        return await $firebase.loadDocumentOrFail<MembershipRequest>(collections.membershipRequests, id)
    }

    /**
     * Get a membership request by user and skill
     */
    const getMembershipRequest = async (userId: UserId, skillId: SkillId): Promise<MembershipRequest | undefined> => {
        const requests = await $firebase.listDocuments<MembershipRequest>(
            query(collections.membershipRequests, where('userId', '==', userId), where('skillId', '==', skillId))
        )

        return requests.pop()
    }

    /**
     * Create a new membership request
     */
    const createMembershipRequest = async (
        skillId: SkillId,
        data: Partial<MembershipRequest>,
        userId?: UserId
    ): Promise<MembershipRequest> => {
        const response = await api.post('membership-requests/create', {
            ...data,
            skillId,
            userId
        })

        return loadMembershipRequest(response.data.id)
    }

    /**
     * Accept a membership request
     */
    const acceptMembershipRequest = async (id: MembershipRequestId): Promise<void> => {
        await api.post(`membership-requests/${id}/accept`)
    }

    /**
     * Reject a membership request
     */
    const rejectMembershipRequest = async (id: MembershipRequestId, message?: string): Promise<void> => {
        await api.post(`membership-requests/${id}/reject`, { message })
    }

    /**
     * Revert a rejected membership request
     */
    const revertRejectedMembershipRequest = async (id: MembershipRequestId): Promise<void> => {
        await api.post(`membership-requests/${id}/revertRejected`)
    }

    return {
        listMembershipRequests,
        loadMembershipRequest,
        getMembershipRequest,
        createMembershipRequest,
        acceptMembershipRequest,
        rejectMembershipRequest,
        revertRejectedMembershipRequest
    }
}
